import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useLazyGetClientServiceCategoriesForFilterQuery } from '@app/api/ClientServices';
import { EDIT_CATEGORY_PATH } from '@app/constants';
import {
  useNewUserAccessRights,
  useUserPrint,
  useWindowSize,
} from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';
import type { NClientServices } from '@app/types/ClientServices';

import { getServiceCategoriesColumns } from '@pages/ServiceCategories/data/columns';

export const useServiceCategories = () => {
  const navigate = useNavigate();
  const { activeBranchId } = useUserPrint();
  const { isMobile } = useWindowSize();
  const { showNotification } = useNotification();
  const [getClientServiceCategories] =
    useLazyGetClientServiceCategoriesForFilterQuery();

  const [isLoading, setIsLoading] = useState(true);
  const [categories, setCategories] = useState<NClientServices.Category[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const {
    userAccessRights: { ClientServices },
  } = useNewUserAccessRights();

  const hasUserRightForModuleView = useMemo(
    () => ClientServices?.IsOn,
    [ClientServices]
  );
  const hasUserRightToRegister = useMemo(
    () => ClientServices?.IsServiceCategories,
    [ClientServices]
  );

  const hasUserRightToEdit = useMemo(
    () => ClientServices?.IsCategoryEdit,
    [ClientServices]
  );

  const getClientServiceCategoriesHandler = useCallback(async () => {
    try {
      setIsLoading(true);

      const response = await getClientServiceCategories({
        ActiveBranchId: activeBranchId,
      }).unwrap();

      setCategories(response);
      setPagination((prev) => ({ ...prev, total: response.length }));
    } catch (error) {
      showNotification(handleCatchError(error), 'warning');
    } finally {
      setIsLoading(false);
    }
  }, [activeBranchId, showNotification, getClientServiceCategories]);

  const handleChange = useCallback((page: number, pageSize: number) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize,
    }));
  }, []);

  const onEdit = useCallback(
    (id: number) => {
      navigate(`${EDIT_CATEGORY_PATH}?id=${id}`);
    },
    [navigate]
  );

  const columns = useMemo(
    () => getServiceCategoriesColumns({ onEdit, hasUserRightToEdit }),
    [onEdit, hasUserRightToEdit]
  );

  return {
    isLoading,
    categories,
    isMobile,
    handleChange,
    pagination,
    hasUserRightForModuleView,
    hasUserRightToRegister,
    getClientServiceCategoriesHandler,
    columns,
  };
};
